import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';

import { ReactComponent as CashWalletCloudUploadIcon } from '@/img/icons/cash-wallet-cloud-upload-icon.svg';
import { ReactComponent as CashWalletFileIcon } from '@/img/icons/cash-wallet-file-icon.svg';
import { ReactComponent as CashWalletDeleteIcon } from '@/img/icons/cash-wallet-delete-icon.svg';

import CashWalletConstants from './CashWalletConstants';

const { INVOICE_ACCEPT, INVOICE_EXTENSION, INVOICE_MAX_SIZE_BYTES, PDF_MAGIC } = CashWalletConstants;

const MAGIC_BYTE_LENGTH = PDF_MAGIC.length;

function hasPdfExtension(name) {
    return String(name || '')
        .toLowerCase()
        .endsWith(INVOICE_EXTENSION);
}

function isPdfContentType(type) {
    return String(type || '')
        .toLowerCase()
        .split(';')[0]
        .trim() === INVOICE_ACCEPT;
}

function readPdfMagic(file) {
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
            const bytes = new Uint8Array(reader.result || []);
            let header = '';
            for (let i = 0; i < bytes.length; i += 1) {
                header += String.fromCharCode(bytes[i]);
            }
            resolve(header === PDF_MAGIC);
        };
        reader.onerror = () => resolve(false);
        reader.readAsArrayBuffer(file.slice(0, MAGIC_BYTE_LENGTH));
    });
}

async function validateInvoice(file) {
    if (!hasPdfExtension(file.name) || !isPdfContentType(file.type)) {
        return false;
    }
    if (file.size > INVOICE_MAX_SIZE_BYTES) {
        return false;
    }
    return readPdfMagic(file);
}

const InvoiceDropzone = ({ file, error, onSelect }) => {
    const { t } = useTranslation('CashWallet');
    const inputRef = useRef(null);
    const [dragActive, setDragActive] = useState(false);

    const handleFiles = async (fileList) => {
        const picked = fileList && fileList[0];
        if (!picked) {
            return;
        }
        const valid = await validateInvoice(picked);
        if (valid) {
            onSelect(picked, null);
        } else {
            onSelect(null, t('Drawer.InvoiceInvalid'));
        }
    };

    const onInputChange = (event) => {
        handleFiles(event.target.files);
        event.target.value = '';
    };

    const onDrop = (event) => {
        event.preventDefault();
        setDragActive(false);
        handleFiles(event.dataTransfer.files);
    };

    const onDragOver = (event) => {
        event.preventDefault();
        setDragActive(true);
    };

    const onDragLeave = (event) => {
        event.preventDefault();
        setDragActive(false);
    };

    const openPicker = () => inputRef.current && inputRef.current.click();

    const onKeyDown = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openPicker();
        }
    };

    if (file) {
        return (
            <div className="cash-wallet-dropzone-wrap">
                <div className="cash-wallet-file-chip">
                    <CashWalletFileIcon className="cash-wallet-file-chip-icon" />
                    <span className="cash-wallet-file-chip-name">{file.name}</span>
                    <button
                        type="button"
                        className="cash-wallet-file-chip-remove"
                        aria-label="remove"
                        onClick={() => onSelect(null, null)}
                    >
                        <CashWalletDeleteIcon className="cash-wallet-file-chip-remove-icon" />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="cash-wallet-dropzone-wrap">
            <div
                className={`cash-wallet-dropzone${dragActive ? ' cash-wallet-dropzone-active' : ''}`}
                onDrop={onDrop}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onClick={openPicker}
                onKeyDown={onKeyDown}
                role="button"
                tabIndex={0}
                aria-label={t('Drawer.UploadTitle')}
            >
                <CashWalletCloudUploadIcon className="cash-wallet-dropzone-icon" />
                <div className="cash-wallet-dropzone-hint">{t('Drawer.DropzoneHint')}</div>
                <Button
                    className="cash-wallet-dropzone-button"
                    variant="outlined"
                    color="primary"
                    sx={{ textTransform: 'none' }}
                    onClick={(event) => {
                        event.stopPropagation();
                        openPicker();
                    }}
                >
                    {t('Drawer.UploadButton')}
                </Button>
                <input
                    ref={inputRef}
                    type="file"
                    accept={INVOICE_ACCEPT}
                    hidden
                    onChange={onInputChange}
                />
            </div>
            {error && <div className="cash-wallet-dropzone-error">{error}</div>}
        </div>
    );
};

export default InvoiceDropzone;
